import { faBook, faCar } from '@fortawesome/pro-solid-svg-icons';
import {
    IonButton,
    IonCheckbox,
    IonCol,
    IonContent,
    IonFab,
    IonFabButton,
    IonFabList,
    IonGrid,
    IonIcon,
    IonItem,
    IonLabel,
    IonPage,
    IonRow,
    IonText,
    IonThumbnail,
    ToastOptions,
    useIonAlert,
    useIonToast,
} from '@ionic/react';
import { chevronForwardOutline, car } from 'ionicons/icons';

import { faBasketShopping } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAtomValue, useSetAtom } from 'jotai';
import { each, filter, groupBy, keys, map, size, sumBy, toInteger, find } from 'lodash/fp';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';
import { useAppConfig, useCompanies, useEmployees, useWorkPlaces } from '../../hooks/useSwr';
import { PointingState } from '../../utils/odooUtils';
import { dateFromOdooToFrench, dateFromOdooToFrenchMedium, floatToTime, nowToOdooDatetime } from '../../utils/date';
import { pointingToEditIdAtom, usersAtom, loadingAtom } from '../../utils/Global';
import odoo, { saveMulti, standardizedOdooPointings } from '../../utils/odoo';
import './ValidationByEmployee.css';
import { OdooContract, OdooPointing, Pointing, PointingToSavePartial, pointingFields } from '../../types/models';
import { IosHeader } from '../../components/IosHeader';
import { Header } from '../../components/Header';
import { FabContact } from '../../components/FabContact';
import { VisibilityField } from '../../components/common/PointingDetail';

export const ValidationByEmployee: React.FC = () => {
    const title = 'Validation';
    const history = useHistory();
    const location = useLocation();
    const { employeeId, state } = useParams<{ employeeId: string; state: string }>();

    const { mainUser } = useAtomValue(usersAtom);
    const [presentToast] = useIonToast();

    const { normalize: employees } = useEmployees();
    const { normalize: companies } = useCompanies();

    const [validationConfirmAlert] = useIonAlert();

    const [contract, setContract] = useState<OdooContract | null>(null);
    const [pointingByWeek, setPointingByWeek] = useState<any>({});
    const [pointingChecked, setPointingChecked] = useState<number[]>([]);
    const setPointingToEditId = useSetAtom(pointingToEditIdAtom);

    const employee = employees[toInteger(employeeId)];

    const onSelectionChange = (pointingIds: number[], checked: boolean) => {
        setPointingChecked(
            checked
                ? [...pointingChecked, ...pointingIds]
                : filter((id: number) => !pointingIds.includes(id), pointingChecked)
        );
    };

    const setLoading = useSetAtom(loadingAtom);
    const searchAndSetPointing = async () => {
        setLoading(true);
        odoo.searchRead(
            'hr.madper.app.pointing.time',
            {
                fields: pointingFields,
                domain: [
                    ['employee_id', '=', employee.id],
                    ['state', '=', state],
                    ['origin_id', '=', false],
                ],
                sort: 'id desc',
            },
            true
        )
            .then((pointings: OdooPointing[]) => {
                setPointingByWeek(
                    groupBy((pointing) => {
                        const dt = DateTime.fromSQL(pointing.date);
                        return dt.startOf('week').toISODate();
                    }, standardizedOdooPointings(pointings))
                );
                setLoading(false);
            })
            .catch((e) => {
                each((errorToast: ToastOptions) => presentToast(errorToast), e.messages);
                setLoading(false);
            });
    };

    useEffect(() => {
        setPointingToEditId(null);
        if (employee) {
            odoo.searchReadOne('hr.madper.contract', {
                fields: ['id', 'name', 'date_start', 'resource_calendar_id'],
                domain: [['employee_id', '=', employee.id]],
                sort: 'id desc',
            })
                .then(setContract)
                .catch((e) => {
                    each((errorToast: ToastOptions) => presentToast(errorToast), e.messages);
                });

            searchAndSetPointing();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]);

    if (!employee || !state) {
        return null;
    }

    const company = companies[employee.company_id[0]];

    const onClickFabCar = () => {
        validationConfirmAlert({
            header: 'Veuillez confirmer le déplacement',
            buttons: [
                {
                    text: 'Annuler',
                    handler: () => {
                        return;
                    },
                },
                {
                    text: 'Valider',
                    handler: async () => {
                        await saveMulti<PointingToSavePartial>(
                            'hr.madper.app.pointing.time',
                            pointingChecked,
                            { travel: 1 },
                            { forwardIp: true, pointingState: 'confirm', bypassValidation: true }
                        );

                        searchAndSetPointing();

                        presentToast({
                            message: 'Déplacement(s) validé(s) avec succès',
                            duration: 3000,
                            position: 'bottom',
                            color: 'success',
                        });
                    },
                },
            ],
        });
    };

    return (
        <IonPage id="validation-page">
            <IosHeader title={title} backText="Retour" />
            <IonContent className="bg-neutral-100">
                <Header title={title} backText="Retour" />

                {state === PointingState.GeiqValidation && (company?.phone || company?.email) && (
                    <FabContact company={company} />
                )}

                {pointingChecked.length !== 0 && (
                    <IonFab slot="fixed" vertical="bottom" horizontal="end" onClick={onClickFabCar}>
                        <IonFabButton>
                            <IonIcon icon={car}></IonIcon>
                        </IonFabButton>
                    </IonFab>
                )}

                <IonGrid className="ion-no-padding pb-4 pt-5 grid-validation-infos-d">
                    <div className="p-5 pb-2">
                        <IonRow className="pb-4 w-full">
                            <IonCol size="auto">
                                <IonThumbnail className="w-28 h-28">
                                    <img
                                        className="rounded-lg h-full"
                                        alt="Employee avatar"
                                        src={
                                            employee.image_128
                                                ? `data:image/jpeg;base64,${employee.image_128}`
                                                : 'https://ionicframework.com/docs/img/demos/avatar.svg'
                                        }
                                    />
                                </IonThumbnail>
                            </IonCol>
                            <IonCol size="8" className="pl-4">
                                <IonLabel className="text-2xl font-bold text-black">{employee.name}</IonLabel>
                                {contract && (
                                    <IonText className="text-gray-500">
                                        <p>{dateFromOdooToFrench(contract.date_start)}</p>
                                    </IonText>
                                )}
                                <IonText className="text-gray-500">
                                    <p>{employee.best_phone}</p>
                                </IonText>

                                {/* add in v2 */}
                                {/* <IonButton shape="round" color="success" className="h-auto w-auto" onClick={() => {}}>
                                    <span className="font-semibold text-sm uppercase">Envoyer un message</span>
                                </IonButton> */}
                            </IonCol>
                        </IonRow>
                    </div>

                    {map(
                        (date: string) => (
                            <PointingDateSection
                                key={date}
                                date={date}
                                weekNumber={DateTime.fromISO(date).weekNumber.toString().padStart(2, '0')}
                                pointings={pointingByWeek[date]}
                                pointingChecked={pointingChecked}
                                onSelectionChange={
                                    (state === PointingState.WaitValidation && onSelectionChange) || undefined
                                }
                            />
                        ),
                        keys(pointingByWeek)
                    )}
                </IonGrid>
                {state === PointingState.WaitValidation && (
                    <div className="madper-button">
                        <IonButton
                            shape="round"
                            type="submit"
                            expand="block"
                            className="btn normal-case"
                            disabled={pointingChecked.length === 0}
                            onClick={() => {
                                validationConfirmAlert({
                                    header: 'Veuillez confirmer la validation',
                                    buttons: [
                                        {
                                            text: 'Annuler',
                                            role: 'cancel',
                                        },
                                        {
                                            text: 'Valider',
                                            role: 'confirm',
                                            handler: async () => {
                                                try {
                                                    await odoo.write('hr.madper.app.pointing.time', pointingChecked, {
                                                        user_adherent_id: mainUser!.uid,
                                                        user_adherent_validation_date: nowToOdooDatetime(),
                                                    });
                                                } catch (e) {
                                                    //TODO: Handle error
                                                }
                                                history.replace('/validation');
                                            },
                                        },
                                    ],
                                });
                            }}
                        >
                            Valider
                        </IonButton>
                    </div>
                )}
            </IonContent>
        </IonPage>
    );
};

export default ValidationByEmployee;

const PointingDateSection: React.FC<{
    date: string;
    weekNumber: string;
    pointings: Pointing[];
    pointingChecked: number[];
    onSelectionChange?: (pointing: number[], selected: boolean) => void;
}> = ({ weekNumber, pointings, pointingChecked, onSelectionChange }) => {
    const totalHours = sumBy((pointing: Pointing) => pointing.working_hours + pointing.working_night_hours, pointings);
    const totalLunch = sumBy((pointing: Pointing) => pointing.lunch + pointing.lunch_night, pointings);

    return (
        <>
            <IonRow className="bg-zinc-300 divide-x divide-gray-400 text-center align-middle ion-align-items-center">
                {onSelectionChange && (
                    <IonCol size="1">
                        <IonCheckbox
                            aria-label="Tout sélectionner"
                            className="m-0 text-center mt-1"
                            onIonChange={(e) => onSelectionChange(map('id', pointings), e.detail.checked)}
                        />
                    </IonCol>
                )}
                <IonCol size="auto" className="px-2">
                    <p className="text-slate-600 text-xs mb-1">n° Semaine</p>
                    <p className=" text-lg font-semibold text-slate-900">{weekNumber}</p>
                </IonCol>
                <IonCol>
                    <p className="text-slate-600 text-xs mb-1">Total</p>
                    <IonLabel className=" text-lg font-semibold text-slate-900">
                        {floatToTime(totalHours, "h'h'mm")}
                    </IonLabel>
                </IonCol>
                <IonCol size="auto" className="px-2">
                    <p className="text-slate-600 text-xs mb-1">Déplacement</p>
                    <p className=" text-lg font-semibold text-slate-900">
                        <FontAwesomeIcon icon={faCar} className="text-black text-xl mr-2" />
                        {size(filter({ travel: 1 }, pointings))}
                    </p>
                </IonCol>
                <IonCol>
                    <p className="text-slate-600 text-xs mb-1">Panier</p>
                    <p className=" text-lg font-semibold text-slate-900">
                        <FontAwesomeIcon icon={faBasketShopping} className="text-black text-xl mr-2" />
                        {totalLunch}
                    </p>
                </IonCol>
            </IonRow>

            {map(
                (pointing) => (
                    <PointingRow
                        key={pointing.id}
                        pointing={pointing}
                        checked={pointingChecked.includes(pointing.id)}
                        onSelectionChange={onSelectionChange}
                    />
                ),
                pointings
            )}
        </>
    );
};

const PointingRow: React.FC<{
    pointing: Pointing;
    checked: boolean;
    onSelectionChange?: (pointing: number[], selected: boolean) => void;
}> = ({ pointing, checked, onSelectionChange }) => {
    const history = useHistory();

    const { data: appConfig } = useAppConfig();
    const { dataWithDisplayName: employees } = useEmployees();
    const { data: companies } = useCompanies();
    const { normalize: workPlaces } = useWorkPlaces();

    const setPointingToEditId = useSetAtom(pointingToEditIdAtom);

    const onClick = () => {
        setPointingToEditId(pointing.id);
        history.push('/saisiedetail', {
            edit: true,
            from: history.location.pathname,
        });
    };

    const employee = find((e) => e.id === pointing.employee_id, employees);
    const company = find((c) => c.id === employee?.company_id[0], companies);

    return (
        <>
            <IonItem
                lines="none"
                className="flex text-center align-middle font-bold ion-no-background"
                style={{ '--inner-padding-end': 0 }}
            >
                {onSelectionChange && (
                    <IonCheckbox
                        labelPlacement="end"
                        aria-label="Sélectionner"
                        className="flex-auto"
                        name={`select-${pointing.id}`}
                        checked={checked}
                        onClick={(e) => {
                            onSelectionChange([pointing.id], !checked);
                        }}
                        justify="start"
                    >
                        <IonLabel color="tertiary" className="pl-2 text-left">
                            <span style={{ textDecoration: 'underline' }}>
                                {dateFromOdooToFrenchMedium(pointing.date)} {':'}
                            </span>{' '}
                            {workPlaces[pointing.workplace_id]?.name}
                        </IonLabel>
                    </IonCheckbox>
                )}

                {!onSelectionChange && (
                    <>
                        <IonLabel color="tertiary" className="pl-2 flex-auto text-left">
                            <span style={{ textDecoration: 'underline' }}>
                                {dateFromOdooToFrenchMedium(pointing.date)} {':'}
                            </span>{' '}
                            {workPlaces[pointing.workplace_id]?.name}
                        </IonLabel>
                    </>
                )}

                <IonIcon
                    onClick={onClick}
                    icon={chevronForwardOutline}
                    className="text-2xl float-right m-0"
                    color="primary"
                    slot="end"
                />
            </IonItem>

            <IonRow
                className={`${
                    !onSelectionChange && 'pl-2'
                } text-center py-1 divide-x divide-gray-400 pr-1 border-b border-gray-400 flex-1`}
            >
                {appConfig?.appMode === 'live' && pointing.input_time_start && pointing.input_time_end && (
                    <IonCol>
                        <IonRow>
                            <IonCol className="text-gray-500 text-sm">Travaillées</IonCol>
                        </IonRow>
                        <IonRow className="divide-x divide-gray-400">
                            <IonCol>{pointing.input_time_start.toFormat("HH'h'mm")}</IonCol>
                            <IonCol>{pointing.input_time_end.toFormat("HH'h'mm")}</IonCol>
                        </IonRow>
                        <IonRow className="text-gray-500 text-sm">
                            <IonCol>Début</IonCol>
                            <IonCol>Fin</IonCol>
                        </IonRow>
                    </IonCol>
                )}
                <IonCol size="7">
                    <IonRow>
                        <IonCol className="text-gray-500 text-sm">Heures</IonCol>
                    </IonRow>
                    <IonRow className="divide-x divide-gray-400">
                        <IonCol>{floatToTime(pointing.working_hours, "h':'mm")}</IonCol>
                        <IonCol>{floatToTime(pointing.working_night_hours, "h':'mm")}</IonCol>
                        <IonCol>{floatToTime(pointing.bad_weather_hours, "h':'mm")}</IonCol>
                        <VisibilityField company={company} companyAttributes={['pointing_rubric_absence_hours_id']}>
                            <IonCol>{floatToTime(pointing.absence_hours, "h':'mm")}</IonCol>
                        </VisibilityField>
                    </IonRow>
                    <IonRow className="text-gray-500 text-sm">
                        <IonCol>Jour</IonCol>
                        <IonCol>Nuit</IonCol>
                        <IonCol>Intemp.</IonCol>
                        <VisibilityField company={company} companyAttributes={['pointing_rubric_absence_hours_id']}>
                            <IonCol>Abs.</IonCol>
                        </VisibilityField>
                    </IonRow>
                </IonCol>
                <IonCol className="text-center w-full">
                    <IonRow>
                        <IonCol className="text-gray-500 text-sm">Paniers</IonCol>
                    </IonRow>
                    <IonRow className="divide-x divide-gray-400">
                        <IonCol className={pointing.lunch ? 'text-black' : 'text-gray-500'}>{pointing.lunch}</IonCol>
                        <IonCol className={pointing.lunch_night ? 'text-black' : 'text-gray-500'}>
                            {pointing.lunch_night}
                        </IonCol>
                    </IonRow>
                    <IonRow className="text-gray-500 text-sm">
                        <IonCol>Jour</IonCol>
                        <IonCol>Nuit</IonCol>
                    </IonRow>
                </IonCol>
                <IonCol className="divide-y divide-gray-400" size="1">
                    <IonRow className="text-center h-1/2">
                        <div className="w-full h-full">
                            <FontAwesomeIcon color={pointing.travel ? 'green' : 'grey'} icon={faCar} />
                        </div>
                    </IonRow>
                    <IonRow className="text-center h-1/2">
                        <div className="w-full h-full">
                            <FontAwesomeIcon color={pointing.employee_note ? 'black' : 'gray'} icon={faBook} />
                        </div>
                    </IonRow>
                </IonCol>
            </IonRow>
        </>
    );
};
